import { Injectable } from '@angular/core';
import { PushNotifications, PermissionStatus } from '@capacitor/push-notifications';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { NGXLogger } from 'ngx-logger';
import { DeviceService } from './device.service';
import { ensureInstallationId, collectDeviceContext, saveServerDeviceId, getServerDeviceId } from '../utils/push-device.utils';
import { AuthService } from './auth.service';
import { FirebaseMessagingService } from './firebase-messaging.service';

@Injectable({ providedIn: 'root' })
export class PushService {
	private initialized = false;

	constructor(
		private readonly deviceService: DeviceService, 
		private readonly logger: NGXLogger, 
		private readonly auth: AuthService,
		private readonly firebaseMessagingService: FirebaseMessagingService
	) {}

	async init(): Promise<void> {
		if (this.initialized) return;
		if (!Capacitor.isNativePlatform()) {
			this.logger.debug('PushService: plataforma no nativa, se omite init');
			this.initialized = true;
			return;
		}

		await this.requestNotificationPermission();
		this.registerListeners();
		if (await this.isLoggedIn()) {
			await this.registerDeviceFlow();
			await this.firebaseMessagingService.subscribeToRecommendedTopics();
		} else {
			this.logger.debug('PushService: sin sesión, se omite registro de device');
		}
		this.initialized = true;
	}

	private async isLoggedIn(): Promise<boolean> {
		try {
			const login = await this.auth.getLogin();
			return Boolean(login?.access_token);
		} catch {
			return false;
		}
	}

	private async requestNotificationPermission(): Promise<void> {
		try {
			const status: PermissionStatus = await PushNotifications.checkPermissions();
			if (status.receive !== 'granted') {
				const res = await PushNotifications.requestPermissions();
				this.logger.info('Push permission result:', res);
			}
			await PushNotifications.register();
			if (Capacitor.getPlatform() === 'ios') {
				await FirebaseMessaging.requestPermissions();
			}
		} catch (error) {
			this.logger.error('Error solicitando permisos de notificación', error);
		}
	}

	private async registerDeviceFlow(): Promise<void> {
		try {
			const installation_id = await ensureInstallationId();
			const { platform, app_version, app_build, timezone, os_version, device_model, locale } = await collectDeviceContext();

			let token = '';
			let notifications_allowed = false;
			try {
				const res = await FirebaseMessaging.getToken();
				token = res?.token ?? '';
				notifications_allowed = Boolean(token);
			} catch (e) {
				this.logger.warn('No se pudo obtener FCM token', e);
			}

			const resp$ = await this.deviceService.registerDevice({
				installation_id,
				platform,
				push_token: token,
				app_version,
				app_build,
				locale,
				timezone,
				os_version,
				device_model,
				notifications_allowed,
			});

			return new Promise<void>((resolve, reject) => {
				resp$.subscribe({
					next: async (device) => {
						await saveServerDeviceId(device.id);
						this.logger.info('Device registrado:', device.id);
						resolve();
					},
					error: (err) => {
						this.logger.error('Error registrando device', err);
						reject(err);
					},
				});
			});
		} catch (error) {
			this.logger.error('registerDeviceFlow error', error);
			throw error;
		}
	}

	private registerListeners(): void {
		FirebaseMessaging.addListener('tokenReceived', async ({ token }) => {
			try {
				if (!(await this.isLoggedIn())) return;
				const installation_id = await ensureInstallationId();
				const resp$ = await this.deviceService.updateToken(installation_id, token);
				resp$.subscribe({ error: (err) => this.logger.warn('updateToken falló', err) });
			} catch (e) {
				this.logger.warn('No se pudo actualizar token', e);
			}
		});

		App.addListener('appStateChange', async ({ isActive }) => {
			if (!isActive) return;
			try {
				if (!(await this.isLoggedIn())) return;
				const existingId = await getServerDeviceId();
				if (!existingId) {
					await this.registerDeviceFlow();
				}

				const installation_id = await ensureInstallationId();
				const info = await App.getInfo();
				const resp$ = await this.deviceService.heartbeat(installation_id, {
					app_version: info.version,
					app_build: Number(info.build) || undefined,
					locale: navigator.language,
				});
				resp$.subscribe({ error: (err) => this.logger.warn('heartbeat falló', err) });

				await this.firebaseMessagingService.subscribeToRecommendedTopics();
			} catch (e) {
				this.logger.warn('Heartbeat error', e);
			}
		});
	}

	async onLogin(): Promise<void> {
		try {
			if (!Capacitor.isNativePlatform()) return;
			if (!(await this.isLoggedIn())) return;
			await this.registerDeviceFlow();
			// Suscribir a topics recomendados después del login
			await this.firebaseMessagingService.subscribeToRecommendedTopics();
		} catch (e) {
			this.logger.warn('onLogin -> registerDeviceFlow error', e);
		}
	}

	async deactivateCurrentDevice(): Promise<void> {
		try {
			const deviceId = await getServerDeviceId();
			if (!deviceId) return;
			const resp$ = await this.deviceService.deactivate(deviceId);
			resp$.subscribe({
				next: () => this.logger.info('Device desactivado'),
				error: (err) => this.logger.warn('No se pudo desactivar device', err),
			});
		} catch (e) {
			this.logger.warn('deactivateCurrentDevice error', e);
		}
	}
}


